import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, ExternalLink, AlertTriangle, RotateCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { api, usePoll, downloadFile } from "@/lib/api";
import { toast } from "sonner";

function Step({ s, i, onToggle }) {
  return (
    <div className={`bg-white border rounded-lg p-5 flex gap-4 transition-colors ${s.done ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`license-step-${i}`}>
      <Checkbox checked={!!s.done} onCheckedChange={(v) => onToggle(i, !!v)} className="mt-1" data-testid={`license-step-${i}-checkbox`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
          <h4 className={`font-semibold ${s.done ? "text-slate-500 line-through" : "text-slate-900"}`}>{s.title}</h4>
          {s.required_for_grants && <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Grant prerequisite</span>}
        </div>
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.description}</p>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
          {s.agency && <span><b className="text-slate-700">Agency:</b> {s.agency}</span>}
          {s.fee && <span><b className="text-slate-700">Fee:</b> {s.fee}</span>}
          {s.timeline && <span><b className="text-slate-700">Time:</b> {s.timeline}</span>}
          {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" />Official site</a>}
        </div>
      </div>
    </div>
  );
}

export default function LicenseGuide() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const load = useCallback(() => api.get(`/licensing/${id}`).then((r) => setDoc(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, doc?.status === "pending", 3000);

  const toggle = async (i, done) => {
    setDoc((d) => ({ ...d, guide: { ...d.guide, steps: d.guide.steps.map((s, j) => (j === i ? { ...s, done } : s)) } }));
    try { await api.patch(`/licensing/${id}/steps/${i}`, { done }); } catch { toast.error("Couldn't save progress"); load(); }
  };

  if (!doc) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const g = doc.guide;
  const steps = g?.steps || [];
  const pct = steps.length ? Math.round((steps.filter((s) => s.done).length / steps.length) * 100) : 0;
  return (
    <div>
      <Link to="/dashboard/licensing" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6" data-testid="back-to-licensing-link"><ArrowLeft className="h-4 w-4" />Licensing guides</Link>
      <div className="eyebrow mb-2">Area 02 · {doc.state}</div>
      <div className="flex flex-wrap justify-between items-end gap-4">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="license-guide-title">{doc.industry} {doc.business_type} licensing</h1>
        {doc.status === "ready" && <Button variant="outline" onClick={() => downloadFile(`/licensing/${id}/pdf`, `${doc.state}_${doc.business_type}_licensing_guide.pdf`.replace(/\s+/g, "_")).catch(() => toast.error("Couldn't download PDF"))} data-testid="license-download-pdf-button"><Download className="h-4 w-4 mr-2" />Download PDF</Button>}
      </div>
      {doc.status === "pending" && (
        <div className="mt-8 bg-white border border-dashed border-emerald-300 rounded-lg p-10 flex items-center gap-4" data-testid="license-guide-loading">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          <div><div className="font-semibold text-slate-900">Building your {doc.state} licensing guide…</div><div className="text-sm text-slate-500">Usually 30–90 seconds.</div></div>
        </div>
      )}
      {doc.status === "error" && <p className="mt-8 text-red-600 flex items-center gap-2"><RotateCw className="h-4 w-4" />Generation failed. Please create a new guide.</p>}
      {doc.status === "ready" && g && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-3" data-testid="license-steps-list">
            {steps.map((s, i) => <Step key={i} s={s} i={i} onToggle={toggle} />)}
          </div>
          <aside className="lg:col-span-4 space-y-4">
            <div className="bg-slate-900 text-white rounded-lg p-6">
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-400">Progress</div>
              <div className="font-display text-5xl font-extrabold mt-3" data-testid="license-progress-percent">{pct}%</div>
              <Progress value={pct} className="h-1.5 mt-4 bg-slate-700" />
              <div className="mt-6 text-sm text-slate-300 space-y-1">
                <div>Est. cost: <b className="text-white">{g.estimated_total_cost}</b></div>
                <div>Est. timeline: <b className="text-white">{g.estimated_timeline}</b></div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6"><p className="text-sm text-slate-600 leading-relaxed">{g.overview}</p></div>
            {g.renewals?.length > 0 && <div className="bg-white border border-slate-200 rounded-lg p-6"><h4 className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-700 mb-3">Renewals</h4><ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-4">{g.renewals.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
            {g.warnings?.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-6"><h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4" />Watch out</h4><ul className="text-sm text-amber-900 space-y-1.5 list-disc pl-4">{g.warnings.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
            <p className="text-xs text-slate-400">AI-generated guidance. Confirm fees and requirements with each agency.</p>
          </aside>
        </div>
      )}
    </div>
  );
}
